<script setup lang="ts">
  import { TALK_USER_DATA_ITEM_TEXT_MAX } from "~/consts/TalkConfig";
  import type { PlanInfo } from "~/entities/PlanInfo";
  import { Talk } from "~/entities/Talk";
  import type { TalkUserData } from "~/entities/TalkUserData";
  import type { TalkRoom } from "~/entities/TalkRoom";
  import type { TalkRoomUser } from "~/entities/TalkRoomUser";

  interface Props {
    planInfo: PlanInfo;
    talkUserData: TalkUserData;
    height: string;
    talkRooms: TalkRoom[];
    talkRoomUsers: Record<string, TalkRoomUser>;
    simplify?: boolean; // true の場合、トークルーム名や下線を省略する
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "select", userData: TalkUserData): Promise<void>;
  }
  const emit = defineEmits<Emits>();

  const getUserName = (userId: string) => {
    return omitString(props.talkRoomUsers[userId]?.userName, 14);
  };
  const getUserImage = (userId: string) => {
    return props.talkRoomUsers[userId]?.userImage;
  };
  const isDMTalkRoom = (talkRoomId: string) => {
    return !!props.talkRooms.find(r => r.talkRoomId === talkRoomId)?.isDirectMessage;
  };
  const getTalkRoomIconSrc = (talkRoomId: String) => {
    return getTalkRoomIcon(props.talkRooms.find(r => r.talkRoomId === talkRoomId)!, props.planInfo);
  };
  const getTalkRoomName = (talkRoomId: String) => {
    return props.talkRooms.find(r => r.talkRoomId === talkRoomId)?.roomName;
  };
  const getTalkText = (talk: Talk) => {
    if (talk.isDeleted()) {
      return "メッセージが削除されました";
    }
    return sanitizeDom(
      decorateByMention(
        omitString(talk.messageType === "text" ? talk.contents : talk.note, TALK_USER_DATA_ITEM_TEXT_MAX),
        talk.mentionUserIds,
        props.talkRoomUsers,
      ),
    );
  };
  const getTalkDate = (talk: Talk) => {
    return FormatDateString(talk.postDateTime, "MM月dd日 HH:mm:ss");
  };
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="talk-user-data-item" @click="emit('select', talkUserData)">
    <div class="d-flex">
      <AtomsUserIcon class="mx-2" :src="getUserImage(talkUserData.talk.userId)" :width="30" :height="30" />
      <div class="talk-user-data-item-user">{{ getUserName(talkUserData.talk.userId) }}</div>
      <v-badge v-if="talkUserData.hasUnread" class="talk-user-data-item-unread" color="red" dot />
    </div>
    <div class="ml-2">
      <div v-if="!simplify" class="d-flex mt-2">
        <div>
          <AtomsUserIcon
            v-if="isDMTalkRoom(talkUserData.talk.talkRoomId)"
            :width="20"
            :height="20"
            :src="getTalkRoomIconSrc(talkUserData.talk.talkRoomId)"
            :is-owner="false"
            class="mt-1 mr-1"
          />
          <AtomsSvgIcon
            v-else
            :width="20"
            :height="20"
            :img-path="getTalkRoomIconSrc(talkUserData.talk.talkRoomId)"
            class="mt-1 mr-1"
          />
        </div>
        <div class="talk-user-data-item-room">{{ getTalkRoomName(talkUserData.talk.talkRoomId) }}</div>
      </div>
      <v-img
        v-if="talkUserData.talk.messageType === 'image'"
        :src="talkUserData.talk.thumbnailImage"
        height="128"
        alt="thumbnail"
      />
      <AtomsPdfFileLabel
        v-if="talkUserData.talk.messageType === 'pdf'"
        :file-name="talkUserData.talk.fileName"
        :has-preview-button="false"
      />
      <div v-if="talkUserData.talk.isDeleted()" class="talk-user-data-item-text-inactive">
        メッセージが削除されました
      </div>
      <div v-else class="talk-user-data-item-text" v-html="getTalkText(talkUserData.talk)"></div>
      <div class="talk-user-data-item-date">{{ getTalkDate(talkUserData.talk) }}</div>
    </div>

    <slot></slot>

    <div v-if="!simplify" class="talk-user-data-item-border"></div>
  </div>
</template>

<style lang="scss" scoped>
  .talk-user-data-item {
    position: relative;
    margin: 8px;
    cursor: pointer;
  }
  .talk-user-data-item-unread {
    position: absolute;
    top: 10px;
    right: 0px;
  }
  .talk-user-data-item-user {
    font-size: 12px;
    width: 100%;
    margin: auto;
  }
  .talk-user-data-item-text {
    font-size: 14px;
    margin: 8px 0;
  }
  .talk-user-data-item-text-inactive {
    font-size: 14px;
    margin: 8px 0;
    color: #777;
  }
  .talk-user-data-item-date {
    font-size: 12px;
  }
  .talk-user-data-item-border {
    margin: 8px;
    border: 1px solid rgb(66, 66, 66);
  }
</style>
